import React, {
    ReactNode, useContext, useEffect, useMemo,
} from "react";
import createId from "../../../create-id";
import EventHandler from "../../../event-handler";
import { Direction } from "../../agent-v3/board-toolbar/SortBy";
import { ExtractParams, PathValues } from "../../../contexts/Routing";

type Renderer<T> = (item: T) => ReactNode;

interface Column<T> {
    key: string;
    title: string;
    renderer: Renderer<T>
    className?: string;
    sortBy?: SortBy<T>;
}

export type ContainsId = {id: unknown};

export type FilterHandler<T, TValue> = (item: T, filter: TValue) => (boolean | undefined | null);

export type FilterRender<TValue> = (filter: TValue, setter: ((value: TValue) => void)) => ReactNode;

export type SerializeType = Record<string, string | undefined> | string | string[];
interface Filter<T, TValue> {
    id: string;
    filterKey?: string;
    filter?: FilterHandler<T, TValue>;
    onReset: () => void;
    isActive?: boolean;
    value: TValue;
    setValue: (value: TValue) => void;
    render?: FilterRender<TValue>;
    serialize?: (value: TValue) => SerializeType;
    deserialize?: (value: SerializeType) => TValue | undefined;
}

type SortBy<T> = (item: T) => string | number | unknown;

type ClickHandler<T> = (item: T) => void;
type CanClickHandler<T> = (item: T) => boolean | undefined;

export interface SortColumn {
    direction: Direction;
    columnId: string;
}

interface ColumnAction<T, TPath extends PathValues = PathValues> {
    id: string;
    altText: string;
    icon: ReactNode;
    isNewTab?: boolean;
    onClick?: ClickHandler<T>;
    canClick: CanClickHandler<T>;
    isDisabled: CanClickHandler<T>;
    link?: TPath;
    getLinkParams?: (item: T) => ExtractParams<TPath>;
    getUrl?: (item: T) => string;
    metricPath?: string[];
}

export interface Options {
    isLoading?: boolean
    error?: Error;
}

export type SelectionChanged<T> = (selection: Set<string>, item?: T[]) => void;

export interface RowSelector<T> {
    onClick?: SelectionChanged<T>;
}

export enum Events {
    UPDATED_DATA = "UPDATED_DATA",
    UPDATED_FILTER = "UPDATED_FILTER",
    UPDATED_SORT = "UPDATED_SORT",
    UPDATED_COLUMN = "UPDATED_COLUMN",
    UPDATED_EMPTY_VIEW = "UPDATED_EMPTY_VIEW",
    UPDATED_COLUMN_ACTION = "UPDATED_COLUMN_ACTION",
    RESET_FILTERS = "RESET_FILTERS",
    UPDATED_SELECTION = "UPDATED_SELECTION",
}

export class TableData<T extends ContainsId> {
    public columns: Column<T>[] = [];

    public filters: Filter<T, unknown>[] = [];

    public columnActions: ColumnAction<T>[] = [];

    public rowSelector?: RowSelector<T>;

    private selectedRows: Set<string> = new Set();

    private sortColumn: SortColumn | undefined;

    public readonly events = new EventHandler<Events>();

    private _emptyElement: ReactNode;

    constructor(
        private readonly data: T[] | undefined,
        public readonly options: Options,
    ) {
        // nothing
    }

    public get isNotEmpty() {
        return this.data?.isNotEmpty;
    }

    public get isEmptyData() {
        return !this.options?.isLoading && this.data?.isEmpty && !this.filters.some(row => row.isActive);
    }

    public get emptyElement(): ReactNode {
        return this._emptyElement;
    }

    public useEmptyElement(element: ReactNode) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            this._emptyElement = element;
            this.events.emit(Events.UPDATED_EMPTY_VIEW);
        }, [element]);
    }

    public useData() {
        const current = this.events.useEvent(
            Events.UPDATED_DATA,
            Events.UPDATED_FILTER,
            Events.UPDATED_SORT,
            Events.UPDATED_COLUMN,
            Events.RESET_FILTERS,
            Events.UPDATED_COLUMN_ACTION,
        );

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            this.events.emit(Events.UPDATED_DATA);
        }, [this.data]);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useMemo(() => {
            let { data } = this;
            this.filters.forEach(filter => {
                data = filter.filter && filter.isActive ? data?.filter(
                    item => (filter.filter as Exclude<typeof filter.filter, undefined>)(item, filter.value),
                ) : data;
            });

            const sortColumn = this.sortColumn ?? (this.columns.isNotEmpty
                ? { columnId: this.columns.firstElement.key, direction: Direction.ASCENDING }
                : undefined);
            if (sortColumn) {
                const column = this.columns.find(col => col.key === sortColumn.columnId);
                if (column?.sortBy) {
                    data = sortColumn.direction === Direction.ASCENDING
                        ? data?.sortBy(column.sortBy)
                        : data?.sortByDescending(column.sortBy);
                }
            }

            return data;
        }, [this.data, this.options, current, this]);
    }

    public useRowSelector(onClick?: SelectionChanged<T>, selection?: Set<string>) {
        this.events.useEvent(Events.UPDATED_COLUMN, Events.UPDATED_SELECTION);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            this.rowSelector = {
                onClick,
            };
            this.events.emit(Events.UPDATED_COLUMN);
            return () => {
                this.rowSelector = undefined;
            };
        }, [this]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (selection && this.selectedRows !== selection) {
                this.selectedRows = selection ?? new Set();
                this.events.emit(Events.UPDATED_SELECTION);
            }
        }, [selection, this]);
    }

    public useRowSelection(): [Set<string> | undefined, (item: T[]) => void, (item: T[]) => void] {
        this.events.useEvent(Events.UPDATED_SELECTION);

        return [
            this.rowSelector ? this.selectedRows : undefined,
            (items: T[]) => {
                items.forEach(item => this.selectedRows.add(item.id as string));
                this.events.emit(Events.UPDATED_SELECTION);
                this.rowSelector?.onClick?.(this.selectedRows, items);
            },
            (items: T[]) => {
                items.forEach(item => this.selectedRows.delete(item.id as string));
                this.events.emit(Events.UPDATED_SELECTION);
                this.rowSelector?.onClick?.(this.selectedRows, items);
            },
        ];
    }

    public useColumn(key: string, title: string, renderer: Renderer<T>, className?: string, sortBy?: SortBy<T>) {
        this.events.useEvent(Events.UPDATED_COLUMN);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            const row: Column<T> = {
                key,
                title,
                renderer,
                className,
                sortBy,
            };
            this.columns.push(row);
            this.events.emit(Events.UPDATED_COLUMN);

            return () => {
                this.columns = this.columns.filter(item => item !== row);
            };
        }, [title, renderer, className, sortBy, this]);
    }

    public useColumnAction<TPath extends PathValues = PathValues>(
        action: Omit<ColumnAction<T, TPath>, "id" | "canClick" | "isDisabled"> & {
            canClick?: CanClickHandler<T>;
            isDisabled?: CanClickHandler<T>;
        },
    ) {
        this.events.useEvent(Events.UPDATED_COLUMN_ACTION);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            const row: ColumnAction<T> = {
                ...action,
                id: createId(),
                canClick: action.canClick ?? (() => true),
                isDisabled: action.isDisabled ?? (() => false),
            };
            this.columnActions.push(row);
            this.events.emit(Events.UPDATED_COLUMN_ACTION);

            return () => {
                this.columnActions = this.columnActions.filter(item => item !== row);
            };
        }, [...Object.values(action), this]);
    }

    public resetFilters() {
        this.events.emit(Events.RESET_FILTERS);
    }

    public useFilter<TValue>(props: {
        filter?: FilterHandler<T, TValue>;
        defaultValue?: TValue;
        dependencies?: React.DependencyList;
        render?: FilterRender<TValue>;
        serialize?: (value: TValue) => SerializeType;
        deserialize?: (value: SerializeType) => TValue | undefined;
        filterKey?: string;
        isEqual?: (left: TValue | undefined, right: TValue | undefined) => boolean | undefined;
    }): [TValue, (value: TValue) => void] {
        this.events.useEvent(Events.UPDATED_FILTER);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const properties = useMemo<Filter<T, TValue | undefined>>(() => ({
            id: createId(),
            value: props.defaultValue,
            isActive: false,
            filter: props.filter as FilterHandler<T, unknown>,
            render: props.render as FilterRender<TValue | undefined>,
            serialize: props.serialize as (value: unknown) => SerializeType,
            deserialize: props.deserialize,
            filterKey: props.filterKey,
            onReset: () => {
                properties.value = props.defaultValue;
                properties.isActive = false;
                this.events.emit(Events.UPDATED_FILTER);
            },
            setValue: (value: TValue | undefined) => {
                properties.value = value;
                properties.isActive = props.isEqual ? !props.isEqual(value, props.defaultValue) : value !== props.defaultValue;
                this.events.emit(Events.UPDATED_FILTER);
            },

        }), [...props.dependencies ?? [], this]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            this.filters.push(properties as Filter<T, unknown>);

            this.events.on(Events.RESET_FILTERS, properties.onReset);
            return () => {
                this.filters = this.filters.filter(item => item !== properties);
                this.events.off(Events.RESET_FILTERS, properties.onReset);
            };
        }, [properties, this]);

        return [
            properties.value as TValue,
            properties.setValue as (value: TValue) => void,
        ];
    }

    public useSortColumn(
        onSort?: ((value: SortColumn | undefined) => void),
    ): [SortColumn | undefined, (column: SortColumn | undefined) => void] {
        this.events.useEvent(Events.UPDATED_SORT);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            const handler = onSort ? () => {
                onSort(this.sortColumn);
            } : undefined;
            if (handler) {
                this.events.on(Events.UPDATED_SORT, handler);
            }
            return handler ? () => {
                this.events.off(Events.UPDATED_SORT, handler);
            } : undefined;
        }, [onSort]);
        return [
            this.sortColumn,
            (column: SortColumn | undefined) => {
                this.sortColumn = column;
                this.events.emit(Events.UPDATED_SORT);
            },
        ];
    }
}

export const TableContext = React.createContext<TableData<ContainsId>>(new TableData<ContainsId>([], {}));

export default function useTableData<T extends ContainsId>(): TableData<T> {
    return useContext(TableContext) as unknown as TableData<T>;
}
